import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Box,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import AuthContext from '../Login/AuthContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import MobileFormSheet from './MobileFormSheet';

const CategorySelectionModal = ({ open, onClose, language = 'EN' }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // On mobile the chosen form opens as an in-app full-screen sheet instead of
  // a new browser tab. `null` = no sheet open.
  const [sheetType, setSheetType] = useState(null);
  const getCategoryTexts = () => {
    switch (language) {
      case 'TH':
        return {
          title: 'เลือกประเภทที่ต้องการเพิ่ม',
          loginRequired: 'ต้องเข้าสู่ระบบ',
          categories: [
            { label: 'อีเว้น/คอนเสิร์ต/กิจกรรม', value: 'event', requiresAuth: false },
            { label: 'รีวิวร้านค้า/ร้านอาหาร', value: 'review', requiresAuth: true },
            { label: 'คูปอง/ส่วนลด', value: 'coupon', requiresAuth: true }
          ]
        };
      case 'CN':
        return {
          title: '选择要添加的类别',
          loginRequired: '需要登录',
          categories: [
            { label: '活动/演唱会/活动', value: 'event', requiresAuth: false },
            { label: '评论商店/餐厅', value: 'review', requiresAuth: true },
            { label: '优惠券/折扣', value: 'coupon', requiresAuth: true }
          ]
        };
      default:
        return {
          title: 'Select Category to Add',
          loginRequired: 'Login Required',
          categories: [
            { label: 'Event/Concert/Activity', value: 'event', requiresAuth: false },
            { label: 'Review Shop/Restaurant', value: 'review', requiresAuth: true },
            { label: 'Coupon/Discount', value: 'coupon', requiresAuth: true }
          ]
        };
    }
  };

  const texts = getCategoryTexts();

  const handleCategoryClick = (category) => {
    // Check if category requires authentication
    if (category.requiresAuth && !isAuthenticated) {
      // Save the return URL to localStorage
      const routeMap = {
        event: '/eventinput',
        review: '/review_input',
        coupon: '/coupon_input'
      };
      localStorage.setItem('returnUrl', routeMap[category.value]);

      // Close modal and navigate to login
      onClose();
      navigate('/login');
      return;
    }

    // Mobile: render the form in-app as a full-screen sheet (no new tab).
    if (isMobile) {
      setSheetType(category.value);
      onClose();
      return;
    }

    // Desktop: keep the existing new-tab behaviour.
    const routeMap = {
      event: '/eventinput',
      review: '/review_input',
      coupon: '/coupon_input'
    };

    window.open(routeMap[category.value], '_blank');
    onClose();
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: 2
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Box>{texts.title}</Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{ ml: 2 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <List sx={{ pt: 0 }}>
          {texts.categories.map((category, index) => (
            <ListItem key={category.value} disablePadding>
              <ListItemButton
                onClick={() => handleCategoryClick(category)}
                sx={{
                  py: 2,
                  borderBottom: index !== texts.categories.length - 1 ? '1px solid #f0f0f0' : 'none',
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{category.label}</span>
                      {category.requiresAuth && !isAuthenticated && (
                        <LockIcon sx={{ fontSize: '1rem', color: '#666' }} />
                      )}
                    </Box>
                  }
                  secondary={
                    category.requiresAuth && !isAuthenticated ? (
                      <Typography variant="caption" sx={{ color: '#f57c00', fontStyle: 'italic' }}>
                        {texts.loginRequired}
                      </Typography>
                    ) : null
                  }
                  primaryTypographyProps={{
                    fontSize: '1rem',
                    fontWeight: 500
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>

    <MobileFormSheet
      open={Boolean(sheetType)}
      type={sheetType}
      language={language}
      onClose={() => setSheetType(null)}
    />
    </>
  );
};

export default CategorySelectionModal;
